<template>
  <DashboardPanel title="业主数据 / 认证">
    <div class="owner-auth">
      <div class="owner-progress">
        <div v-for="item in owners" :key="item.label" class="owner-progress__item" :title="`${item.label}：${item.value} 户（${item.percent}）`">
          <div class="owner-progress__label"><i :class="item.icon" :style="{ color: item.color }"></i><span>{{ item.label }}</span><strong>{{ item.value }}<small>户</small></strong></div>
          <div class="owner-progress__bar"><span :style="{ width: item.percent, background: item.color }"></span><em>{{ item.percent }}</em></div>
        </div>
        <div class="owner-rate"><span><i class="el-icon-success"></i>认证率</span><strong>{{ authRate }}</strong></div>
        <div class="owner-rate__bar"><span :style="{ width: authRate }"></span></div>
        <div class="owner-pages"><i></i><i></i><i></i><i></i></div>
      </div>
      <div class="owner-trend">
        <div class="owner-trend__title">查询期间认证趋势（户）</div>
        <div ref="chart" class="owner-trend__chart"></div>
      </div>
    </div>
  </DashboardPanel>
</template>

<script>
import * as echarts from 'echarts'
import DashboardPanel from './DashboardPanel.vue'
import { dashboardTooltip, formatNumber, formatPercent, toNumber } from '../dashboardFormat'
export default {
  name: 'OwnerAuth', components: { DashboardPanel },
  props: { data: { type: Object, default: () => ({}) } },
  data: () => ({ chart: null }),
  computed: {
    owners() {
      const summary = this.data.summary || {}
      const total = toNumber(summary.totalOwners)
      const verified = toNumber(summary.verifiedOwners)
      const pending = toNumber(summary.pendingOwners)
      const rejected = toNumber(summary.rejectedOwners)
      const unverified = Math.max(total - verified, 0)
      return [
        { label: '已认证业主', value: this.format(verified), percent: this.percent(verified, total), color: '#438df3', icon: 'el-icon-user-solid' },
        { label: '未认证业主', value: this.format(unverified), percent: this.percent(unverified, total), color: '#f5a23c', icon: 'el-icon-user-solid' },
        { label: '待审核', value: this.format(pending), percent: this.percent(pending, total), color: '#39bdc9', icon: 'el-icon-time' },
        { label: '认证失败', value: this.format(rejected), percent: this.percent(rejected, total), color: '#ef5b4f', icon: 'el-icon-warning-outline' }
      ]
    },
    authRate() {
      const summary = this.data.summary || {}
      return this.percent(toNumber(summary.verifiedOwners), toNumber(summary.totalOwners))
    },
    trend() {
      return Array.isArray(this.data.authTrend) ? this.data.authTrend : []
    }
  },
  watch: { data: { deep: true, handler() { this.renderChart() } } },
  mounted() { this.renderChart(); window.addEventListener('resize', this.resizeChart) },
  beforeDestroy() { window.removeEventListener('resize', this.resizeChart); if (this.chart) this.chart.dispose() },
  methods: {
    renderChart() {
      if (!this.chart) this.chart = echarts.init(this.$refs.chart)
      const values = this.trend.map(item => toNumber(item.verifiedCount === undefined ? item.authCount : item.verifiedCount))
      this.chart.setOption({
        grid: { top: 23, right: 8, bottom: 24, left: 34 },
        tooltip: dashboardTooltip({
          trigger: 'axis',
          axisPointer: { type: 'shadow' },
          formatter: params => {
            const item = (Array.isArray(params) ? params[0] : params) || {}
            return `${item.axisValue || ''}<br/>${item.marker || ''}新增认证：${formatNumber(item.value)} 户`
          }
        }),
        xAxis: { type: 'category', data: this.trend.map(item => String(item.bucket || '').slice(5)), axisLine: { lineStyle: { color: '#35566c' } }, axisTick: { show: false }, axisLabel: { color: '#a9bac7', fontSize: 10 } },
        yAxis: { type: 'value', min: 0, max: Math.max(10, Math.ceil(Math.max.apply(null, values.concat([0])) / 10) * 10), splitLine: { lineStyle: { color: 'rgba(79,132,166,.16)', type: 'dashed' } }, axisLabel: { color: '#9fb3c2', fontSize: 10 } },
        series: [{ name: '新增认证', type: 'bar', barWidth: 16, data: values, itemStyle: { color: '#3289ee' }, label: { show: true, position: 'top', color: '#d7e5f0', fontSize: 9 } }]
      }, true)
    },
    resizeChart() { if (this.chart) this.chart.resize() },
    format(value) { return formatNumber(value) },
    percent(value, total) { return formatPercent(value, total) }
  }
}
</script>

<style lang="scss" scoped>
.owner-auth { display: grid; grid-template-columns: 49% 51%; height: 100%; padding: 0 10px 7px; }
.owner-progress { position: relative; padding: 14px 12px 0 0; border-right: 1px solid rgba(76, 128, 162, .32); }
.owner-progress__item { margin-bottom: 15px; }
.owner-progress__label { display: grid; grid-template-columns: 20px 1fr auto; align-items: center; color: #c8d5df; font-size: 12px; }
.owner-progress__label i { font-size: 16px; }
.owner-progress__label strong { color: #ecf4fb; font-size: 14px; font-variant-numeric: tabular-nums; }
.owner-progress__label small { margin-left: 3px; font-size: 9px; font-weight: 400; }
.owner-progress__bar { display: flex; align-items: center; height: 6px; margin: 7px 0 0 20px; background: rgba(64, 98, 122, .55); border-radius: 4px; }
.owner-progress__bar span { height: 100%; border-radius: 4px; box-shadow: 0 0 7px currentColor; }
.owner-progress__bar em { margin-left: 7px; color: #9cb0bd; font-size: 9px; font-style: normal; }
.owner-rate { display: flex; align-items: center; justify-content: space-between; color: #becdd8; font-size: 12px; }
.owner-rate i { margin-right: 8px; color: #3bc873; font-size: 16px; }
.owner-rate strong { color: #39ce70; font-size: 16px; }
.owner-rate__bar { height: 6px; margin: 6px 0 0 20px; background: rgba(64, 98, 122, .55); border-radius: 4px; }
.owner-rate__bar span { display: block; width: 80.3%; height: 100%; background: #3bc873; border-radius: 4px; }
.owner-pages { position: absolute; right: 0; bottom: 1px; left: 0; text-align: center; }
.owner-pages i { display: inline-block; width: 7px; height: 7px; margin: 0 5px; border-radius: 50%; background: #344e62; }
.owner-pages i:first-child { background: #3682b6; }
.owner-trend { min-width: 0; padding-left: 12px; }
.owner-trend__title { height: 25px; padding-top: 9px; color: #b7c9d7; font-size: 11px; text-align: center; }
.owner-trend__chart { height: calc(100% - 25px); }
</style>
